import type { GatewayBrowserClient } from "../gateway.ts";
import {
  searchSessionsDeepBatch,
  type DeepBatchSessionSource,
} from "../chat/global-search-deep-batch.ts";

// JAISIU-3652 — alias for the deep-batch session-source adapter so
// callers (the host) can stay inside the controllers module's
// vocabulary without reaching into the chat directory's type names.
// The two names refer to the same record shape.
export type GlobalSearchDeepSource = DeepBatchSessionSource;

/**
 * UX-R7 / JAISIU-3297 — global search across ALL operator chats.
 *
 * The sidebar's existing `<input type="search">` only filters the already-loaded
 * catalog (`loadChatHistoryCatalog` caps at 80 rows). Operators with longer
 * histories lose access to anything that scrolls off. The gateway already
 * exposes `conversations.search`, which walks every session in the on-disk
 * store, scans full transcripts, and ranks results by recency. This
 * controller is the typed client wrapper that drives the Cmd/Ctrl+K modal.
 *
 * Wire-shape contract (verbatim from `src/gateway/server-methods/sessions.ts`):
 *
 *   request  { query: string; daysBack?: number; maxResults?: number; sessionKinds?: string[] }
 *   response { results: Array<SearchResult>; query: string }
 *
 *   type SearchResult = {
 *     sessionKey: string;
 *     sessionLabel: string;
 *     messageIndex: number;
 *     role: "user" | "assistant" | "system";
 *     text: string;          // first 200 chars of the matched message
 *     matchedText: string;   // 50-char context window with "…" prefixes
 *     timestamp?: number;    // message createdAt or session updatedAt
 *   };
 *
 * The wire rejects queries < 2 chars with an empty `results` array
 * (the handler short-circuits before reading the store).
 */

export type GlobalSearchResult = {
  sessionKey: string;
  sessionLabel: string;
  messageIndex: number;
  role: "user" | "assistant" | "system";
  /** First 200 chars of the matched message (from the gateway). */
  text: string;
  /** 50-char context window with "…" prefixes, as produced by the gateway. */
  matchedText: string;
  timestamp?: number;
};

/**
 * Result row grouped under one chat. The modal renders one
 * `<section class="fleet-global-search__group">` per group so the
 * operator can scan "which chat did I see this in" at a glance.
 *
 * `entries` is sorted by `messageIndex` ascending so the conversation
 * timeline is preserved within a group (the gateway returns hits sorted
 * only across the whole result set, not within a session).
 */
export type GlobalSearchGroup = {
  sessionKey: string;
  sessionLabel: string;
  entries: GlobalSearchResult[];
};

export type GlobalSearchState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  /** Echoed back from the gateway on success. Empty string when rejected (<2 chars). */
  globalSearchQuery: string;
  globalSearchResults: GlobalSearchResult[];
  globalSearchLoading: boolean;
  globalSearchError: string | null;
  /** True while the deep-batch walker is still paging older messages. */
  globalSearchDeepLoading?: boolean;
  /**
   * JAISIU-3652 — optional adapter that hands the deep-batch
   * walker the slice of host state it needs (sessions, live
   * cache, client, connection). The host installs this lazily
   * on every modal keystroke so a closed modal does not retain
   * a live adapter closure. When undefined the controller
   * skips the deep pass entirely — the gateway results still
   * render normally.
   */
  globalSearchDeepSource?: DeepBatchSessionSource;
};

const MAX_RESULTS = 50;

/**
 * Run a full-transcript search across every chat the operator has
 * access to. Returns true when the request fired (even if the gateway
 * later rejects it); false when the gate is closed (no client /
 * disconnected / blank query).
 *
 * Caller is responsible for any debouncing — see
 * `runDebouncedGlobalSearch` below for the module-scope closure used
 * by the modal.
 */
export async function runGlobalSearch(state: GlobalSearchState, query: string): Promise<boolean> {
  if (!state.client || !state.connected) {
    state.globalSearchError = "Not connected to gateway";
    return false;
  }
  const trimmed = query.trim();
  if (!trimmed) {
    state.globalSearchQuery = "";
    state.globalSearchResults = [];
    state.globalSearchError = null;
    return false;
  }
  state.globalSearchLoading = true;
  state.globalSearchError = null;
  state.globalSearchQuery = trimmed;
  let gatewayResults: GlobalSearchResult[] = [];
  try {
    const res = await state.client.request<{
      results?: GlobalSearchResult[];
      query?: string;
    }>("conversations.search", {
      query: trimmed,
      maxResults: MAX_RESULTS,
    });
    gatewayResults = Array.isArray(res?.results) ? res.results : [];
    // Optimistically publish the gateway rows so the modal renders
    // immediately. The deep walker (JAISIU-3652) appends any
    // additional matches on a second pass; the modal re-renders
    // when the merged result is written back below.
    state.globalSearchResults = gatewayResults;
  } catch (err) {
    state.globalSearchError = err instanceof Error ? err.message : String(err);
    state.globalSearchResults = [];
    return false;
  } finally {
    state.globalSearchLoading = false;
  }
  // JAISIU-3652 — kick the deep-batch walker against any session
  // whose body is not yet in the local cache. The walker is
  // read-only w.r.t. the host's `chatMessagesBySession` map; it
  // accumulates an ephemeral per-session text index and emits
  // rows that the gateway missed. We do NOT block the gateway
  // result on the walker — the modal already shows the gateway
  // hits above; the walker enriches them in the background.
  if (state.globalSearchDeepSource) {
    void runGlobalSearchDeepBatch(state, gatewayResults, trimmed);
  }
  return true;
}

/**
 * JAISIU-3652 — best-effort enrichment pass. The walker is allowed
 * to fail silently (a disconnected gateway, a session list the
 * host no longer knows about, a per-query in-flight collision);
 * in every case the modal still shows the gateway rows. On
 * success we merge the walker's rows into the existing
 * `globalSearchResults` via `mergeGlobalSearchResults` so the
 * operator sees one deduplicated list.
 */
async function runGlobalSearchDeepBatch(
  state: GlobalSearchState & { globalSearchDeepSource?: GlobalSearchDeepSource },
  gatewayResults: readonly GlobalSearchResult[],
  query: string,
): Promise<void> {
  const source = state.globalSearchDeepSource;
  if (!source) return;
  // The host might have closed the modal between the gateway RPC
  // and the walker kicking off. `connected` guards that path:
  // when the host tears down the connection we leave the modal
  // showing the gateway rows and skip the walker.
  if (!source.isConnected()) return;
  state.globalSearchDeepLoading = true;
  try {
    const deep = await searchSessionsDeepBatch(source, query);
    if (deep.length === 0) return;
    state.globalSearchResults = mergeGlobalSearchResults(
      state.globalSearchResults,
      deep,
    );
  } catch {
    // Best-effort. A walker failure must not corrupt the gateway
    // rows that are already on screen.
  } finally {
    state.globalSearchDeepLoading = false;
  }
}

/** Empty every search field. Call on modal close or ESC. */
export function clearGlobalSearch(state: GlobalSearchState): void {
  state.globalSearchQuery = "";
  state.globalSearchResults = [];
  state.globalSearchLoading = false;
  state.globalSearchError = null;
}

/**
 * JAISIU-3652 — merge the gateway result set with the deep-batch
 * walker result set into one flat list, preserving the gateway's
 * row order and deduplicating against the deep side by
 * `(sessionKey, messageIndex)`. A deep hit whose key+index matches
 * a gateway hit is dropped because the gateway row already carries
 * a fresher `matchedText` + `text` window (the deep walker only
 * has the lowercased blob). A deep hit with no gateway counterpart
 * is appended at the end so the modal can render every match the
 * walker surfaced.
 *
 * Pure helper. The host (`app.ts`) calls it after the gateway
 * RPC settles AND after the deep walker settles (whichever comes
 * second), so the modal renders once with the union. Exported for
 * unit testing — the deep-batch test suite asserts the dedupe and
 * the order-preservation contracts.
 */
export function mergeGlobalSearchResults(
  gateway: readonly GlobalSearchResult[],
  deep: readonly GlobalSearchResult[],
): GlobalSearchResult[] {
  const out: GlobalSearchResult[] = [];
  // `seen` is the dedupe index. Two rows collide iff their
  // (sessionKey, messageIndex) pair matches. We do NOT compare
  // `text` — the gateway and the deep walker can produce slightly
  // different windows around the same hit and we want the gateway's
  // fresher window to win, not a deep-walker stale one.
  const seen = new Set<string>();
  for (const r of gateway) {
    if (!r.sessionKey) continue;
    const key = `${r.sessionKey}\u0000${r.messageIndex}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  for (const r of deep) {
    if (!r.sessionKey) continue;
    const key = `${r.sessionKey}\u0000${r.messageIndex}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

/**
 * Pure helper: group flat result rows by `sessionKey`, preserving the
 * gateway's recency order across groups and the message timeline within
 * each group. Exported for unit testing per the UX-R7 exit criteria.
 */
export function groupSearchResultsBySession(
  results: readonly GlobalSearchResult[],
): GlobalSearchGroup[] {
  const order: string[] = [];
  const byKey = new Map<string, GlobalSearchGroup>();
  for (const r of results) {
    let group = byKey.get(r.sessionKey);
    if (!group) {
      group = {
        sessionKey: r.sessionKey,
        sessionLabel: r.sessionLabel,
        entries: [],
      };
      byKey.set(r.sessionKey, group);
      order.push(r.sessionKey);
    }
    group.entries.push(r);
  }
  for (const key of order) {
    const group = byKey.get(key);
    if (!group) {
      continue;
    }
    group.entries.sort((a, b) => a.messageIndex - b.messageIndex);
  }
  return order.map((key) => byKey.get(key)!).filter((g): g is GlobalSearchGroup => !!g);
}

/**
 * Render the group's most recent timestamp as a relative-time label
 * (e.g. "3m", "2h", "Sep 5"). Pure, exported for unit testing.
 */
export function formatGlobalSearchGroupTime(
  timestamp: number | undefined,
  nowMs: number = Date.now(),
): string {
  if (!timestamp) {
    return "";
  }
  const diff = nowMs - timestamp;
  if (diff < 60_000) {
    return "just now";
  }
  if (diff < 3_600_000) {
    return `${Math.floor(diff / 60_000)}m`;
  }
  if (diff < 86_400_000) {
    return `${Math.floor(diff / 3_600_000)}h`;
  }
  return new Date(timestamp).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// ───── debounce ─────
// Mirrors `debouncedLoadLlmStats` in `ui/src/ui/app-render.ts:198-205`:
// module-scope closure + 250ms (UX-R7 exit criteria specifies 250ms, not
// the 400ms used by llm-stats). One shared timer per process is enough;
// only one global-search modal can be open at a time.

let globalSearchDebounceTimeout: ReturnType<typeof setTimeout> | null = null;

/** Run a search after a 250ms idle window, cancelling any in-flight timer. */
export function runDebouncedGlobalSearch(
  state: GlobalSearchState,
  query: string,
  delayMs: number = 250,
): void {
  if (globalSearchDebounceTimeout) {
    clearTimeout(globalSearchDebounceTimeout);
    globalSearchDebounceTimeout = null;
  }
  globalSearchDebounceTimeout = setTimeout(() => {
    globalSearchDebounceTimeout = null;
    void runGlobalSearch(state, query);
  }, delayMs);
}

/** Cancel any pending debounced search. Called on modal close / ESC. */
export function cancelDebouncedGlobalSearch(): void {
  if (globalSearchDebounceTimeout) {
    clearTimeout(globalSearchDebounceTimeout);
    globalSearchDebounceTimeout = null;
  }
}
